import { Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';
import { ChatApiService } from './chat-api.service';
import { UserService } from './user.service';
import { SocketService } from './socket.service';

export interface ChatPeer {
  id: any;
  name: string;
  avatar?: string;
  online?: boolean;
  lastLogin?: string;
  lastLogout?: string;
}

export interface ChatMessage {
  id: any;
  mine: boolean;          // true = wiadomość zalogowanego użytkownika
  body: string;
  createdAt: number;      // ms
  clientId?: string;      // do dedupu optymistycznej wiadomości z echem socketu
  pending?: boolean;      // wysłana optymistycznie, jeszcze bez potwierdzenia z API
  error?: boolean;        // wysyłka nie powiodła się — bez retry
  attachmentUrl?: string;   // presigned GET (S3) lub lokalny dataURL (optymistycznie)
  attachmentType?: string;  // np. image/png
}

export interface ChatWindow {
  peer: ChatPeer;
  messages: ChatMessage[];
  minimized: boolean;
  unread: number;
  draft: string;
  openOrder?: number;     // kolejność otwarcia/użycia — im mniejsza, tym „starsze" okno
  loaded?: boolean;       // czy historia została już pobrana z API
  /**
   * Czy okno ma prawo pokazać się w doku (patrz `dockWindows`). Samo wejście na /chat
   * tworzy okna dla przeglądanych rozmów — te mają `false`, żeby po wyjściu ze strony
   * nie zostawiały kafli. Podnosimy na `true` dopiero przy świadomym otwarciu czatu,
   * wysłaniu wiadomości albo jej otrzymaniu.
   */
  docked?: boolean;
  peerTyping?: boolean;   // rozmówca właśnie pisze (event chat:typing)
  showJump?: boolean;     // widoczny przycisk „przejdź do najnowszych"
  jumpCount?: number;     // ile nowych wiadomości od momentu odscrollowania
  error?: string;         // klucz i18n ostatniej nieudanej akcji (pokazywany nad polem wpisywania)
  /**
   * Trwa PIERWSZE pobranie historii tej rozmowy.
   *
   * Osobne od `loaded`, choć to kuszące: `loaded` znaczy „już zapytaliśmy" i jest
   * ustawiane PRZED żądaniem jako strażnik przed dublowaniem go. Widok potrzebuje
   * czegoś innego — „czy mamy już odpowiedź" — i tylko to zna prawdę o tym, czy pusty
   * ekran rozmowy znaczy „ładuje się", czy „nic tu nie napisano".
   */
  loadingHistory?: boolean;
  loadingOlder?: boolean; // trwa dociąganie starszej porcji historii (scroll w górę)
  noMoreOlder?: boolean;  // backend nie ma już nic starszego — nie pytamy ponownie
}

export interface ChatContact extends ChatPeer {
  email?: string;
  lastMessage?: string;
  time?: string;
  unread?: number;
}

/**
 * Serwis czatu opiekunów — podłączony do API (ChatApiService) + realtime przez SocketService.
 * Trzyma otwarte okna doku (max 2 rozwinięte) oraz listę konwersacji dla widoku /chat.
 */
@Injectable({ providedIn: 'root' })
export class ChatService {
  public windows: ChatWindow[] = [];
  private readonly MAX_EXPANDED = 2;
  /**
   * Ile okien/kafli czatu może być NAJWYŻEJ widocznych w doku naraz.
   * ZMIEŃ TĘ JEDNĄ LICZBĘ, żeby zmienić limit — nic więcej nie trzeba ruszać.
   * Nadwyżkę ukrywamy: najstarsze (najdawniej używane) rozmowy przestają się
   * renderować, ale zostają w `windows` razem z historią i wpisanym szkicem, więc
   * wracają, gdy któraś z nowszych zostanie zamknięta.
   */
  public readonly MAX_DOCK_WINDOWS = 5;
  /** Rosnący znacznik kolejności otwarcia/użycia okna — wyznacza, co jest „najstarsze". */
  private windowSeq = 0;
  /** Limit długości treści wiadomości — backend odrzuca dłuższe (400 Bad Request). */
  public readonly MAX_MESSAGE_LENGTH = 1000;
  /**
   * Ile wiadomości ciągniemy w JEDNEJ porcji — tak przy wejściu do rozmowy (`loadMessages`),
   * jak i przy dociąganiu starszych (`loadOlder`). Wcześniej historia kończyła się na tej
   * pierwszej porcji: parametr `before` istniał w ChatApiService, ale nikt go nie podawał,
   * więc po odświeżeniu strony starszej korespondencji nie dało się już zobaczyć.
   * ZMIEŃ TĘ JEDNĄ LICZBĘ, żeby zmienić rozmiar porcji.
   */
  public readonly MESSAGES_PAGE_SIZE = 30;
  /** Formaty przyjmowane przez endpoint załączników bez konwersji. */
  private readonly ATTACHMENT_TYPES = ['image/png', 'image/jpeg'];
  /**
   * Maks. rozmiar wysyłanego obrazu. Uwaga: obraz jedzie jako dataURL (base64)
   * w JSON-ie, więc ładunek jest ~33% większy od pliku — ten limit stosujemy
   * zarówno do pliku (ścieżka bez konwersji), jak i do długości dataURL-a po
   * rekompresji, żeby nie przekroczyć limitu body po stronie serwera.
   */
  private readonly MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
  /** Dłuższy bok obrazu po rekompresji — wystarcza do podglądu, mocno tnie rozmiar. */
  private readonly ATTACHMENT_MAX_DIMENSION = 2048;
  private seq = 1;

  public showContacts = false;
  /**
   * Czy dok jest zwinięty („rolka" — schowane wszystkie okna i kafle, zostaje sam
   * mały przycisk). Stan trzymamy w serwisie, nie w komponencie: dok jest ukrywany
   * na /chat (`*ngIf`), więc pole komponentu wracałoby do domyślnej wartości.
   */
  public dockCollapsed = false;
  /** Klucz localStorage dla `dockEnabled` — trzyma WYŁĄCZENIE, więc brak wpisu = dymki włączone. */
  private readonly DOCK_OFF_KEY = 'chatDockOff';
  /**
   * Czy dymki czatu są w ogóle pokazywane (przełącznik w menu konta). W odróżnieniu od
   * `dockCollapsed`, które chowa same kafle, `false` chowa CAŁY dok — kafle i okna.
   * Stan pamiętamy między sesjami: to preferencja użytkownika, a nie chwilowy układ okien.
   *
   * Ukrycie jest wyłącznie wizualne (klasa CSS w chat-dock — patrz komentarz przy
   * `.chat-dock-off`): okna, historia i liczniki żyją dalej, więc włączenie nie pobiera
   * niczego ponownie z API. Badge przy pozycji „Czat" w menu (`unreadTotal`) liczy się
   * z `contacts`, więc działa niezależnie od tej flagi — sygnał o nowych wiadomościach
   * nie ginie razem z dymkami.
   */
  public dockEnabled = this.readDockEnabled();
  public contacts: ChatContact[] = [];
  public activeConversation: ChatWindow | null = null;

  /**
   * Czy PIERWSZE pobranie listy rozmów już się rozstrzygnęło.
   *
   * Pusta lista przed odpowiedzią jest nie do odróżnienia od „nie ma żadnych rozmów",
   * a widok reagował na to dwoma nieprawdami naraz: komunikatem o braku wyników
   * wyszukiwania i prośbą „Wybierz rozmowę", choć nie było jeszcze czego wybierać.
   *
   * Ustawiana raz i już nie cofana: odpytywanie cykliczne woła `loadConversations`
   * co kilkadziesiąt sekund, a lista nie ma wtedy wracać do szkieletu.
   */
  public conversationsLoaded = false;

  /**
   * Zdarzenie „doszła wiadomość do okna" — komponenty sterują scrollem:
   * initial (załadowana historia) i mine → auto-dół; cudza → auto-dół tylko
   * gdy użytkownik jest przy dole, inaczej przycisk „przejdź do najnowszych".
   */
  public messageAdded = new Subject<{ win: ChatWindow; mine: boolean; initial?: boolean }>();

  private currentUserId: any = null;
  private initialized = false;
  private lastTypingSent = 0;
  private typingTimers: { [peerId: string]: any } = {};

  /**
   * Co ile odpytywać `GET /chat/conversations`. Czat NIE MOŻE opierać się wyłącznie na
   * evencie `chat:message`: gdy backend go nie wyśle (albo socket jest rozłączony), nic
   * nie pojawia się do czasu F5 — ani kafel w doku, ani licznik nieprzeczytanych. Ten
   * odpyt jest zapasową ścieżką; przy działającym sockecie jest bezczynny (patrz
   * `signalUnread` — kafel i tak już istnieje). ZMIEŃ TĘ JEDNĄ LICZBĘ, żeby zmienić tempo.
   */
  private readonly CONVERSATIONS_POLL_MS = 20000;
  private pollTimer: any = null;
  /** Blokada nakładania się odpytań, gdy backend odpowiada wolniej niż interwał. */
  private pollInFlight = false;
  /**
   * Ostatni licznik nieprzeczytanych ZNANY Z BACKENDU, per rozmówca. Punkt odniesienia dla
   * „czy przyszło coś nowego": sygnalizujemy tylko WZROST względem tej wartości, a nie samo
   * `unread > 0`. Dzięki temu kafel zamknięty przez użytkownika nie wraca przy każdym
   * odpytaniu, a zaległe nieprzeczytane nie wyskakują przy wejściu na stronę.
   */
  private unreadBaseline: { [peerId: string]: number } = {};
  /**
   * Czy `unreadBaseline` jest już wczytany dla ZNANEGO użytkownika (patrz `readBaseline`).
   * Dopóki nie jest, `detectNewUnread` tylko zasiewa stan w pamięci i nic nie sygnalizuje —
   * bez ID zalogowanego nie wiadomo, czyj punkt odniesienia porównywać.
   */
  private unreadBaselineReady = false;
  /**
   * Prefiks klucza w `localStorage` (dopisywane jest ID użytkownika), pod którym
   * `unreadBaseline` PRZEŻYWA wylogowanie i odświeżenie strony.
   *
   * Wcześniej punkt odniesienia powstawał z PIERWSZEJ odpowiedzi po zalogowaniu, więc
   * wiadomość odebrana w czasie, gdy użytkownik był wylogowany, od razu wchodziła do
   * punktu odniesienia i nigdy nie zapalała kafla — o rozmowie dało się dowiedzieć
   * dopiero z licznika przy pozycji „Czat" albo po wejściu na /chat.
   *
   * `purgeAuth()` kasuje wyłącznie token, a nie cały `localStorage`, więc wpis przeżywa
   * wylogowanie i po ponownym zalogowaniu mamy z czym porównać stan z backendu.
   */
  private readonly UNREAD_BASELINE_KEY = 'chatUnreadBaseline_';

  constructor(
    private chatApi: ChatApiService,
    private userService: UserService,
    private socketService: SocketService,
    private ngZone: NgZone,
  ) {
    this.init();
  }

  /** Inicjalizacja: nasłuch usera i socketu. Dane ładują się dopiero, gdy znane jest ID zalogowanego. */
  init(): void {
    if (this.initialized) { return; }
    this.initialized = true;

    // KLUCZOWE: po odświeżeniu strony populate() ładuje usera ASYNCHRONICZNIE
    // (getMyData → setAuth). Jednorazowy synchroniczny odczyt dawał tu {} →
    // currentUserId=null na zawsze → mine=false dla całej historii (wszystko po
    // lewej) i sztuczne konwersacje „z samym sobą" z echa socketu. Subskrybujemy
    // BehaviorSubject i (prze)ładowujemy stan czatu, gdy ID stanie się znane.
    this.userService.currentUser.subscribe((cu: any) => {
      // Pacjent (ROLE_API_USER) czatu nie ma — trasa /chat jest w routingu dostępna
      // dopiero od opiekuna w górę. Bez tej bramki serwis i tak odpytywał /chat/*,
      // dostawał 403, a interceptor kasował na tym CAŁĄ sesję, więc pacjent wylatywał
      // z aplikacji przy każdym wejściu (patrz http.token.interceptor.ts).
      if (cu && cu.role === 'ROLE_API_USER') { this.endSession(); return; }

      const id = cu && cu.user_id ? (cu.user_id.id ?? cu.user_id) : null;
      // Puste `{}` = purgeAuth, czyli wylogowanie. Odpytywanie musi tu zgasnąć:
      // ścieżka przez interceptor NIE przeładowuje strony (w odróżnieniu od
      // NewHeaderComponent.finishLogout), więc timer chodził dalej i odpytywał
      // /chat/conversations co 20 s już na ekranie logowania.
      if (id == null) { this.endSession(); return; }
      if (String(id) === String(this.currentUserId)) { return; }
      this.currentUserId = id;
      this.chatApi.currentUserId = id;

      // Inny użytkownik = inne liczniki, więc punkt odniesienia bierzemy Z JEGO wpisu.
      // Nie z pierwszej odpowiedzi backendu: wtedy wszystko, co przyszło w czasie
      // wylogowania, od razu stawało się „już widziane" i nie zapalało kafla.
      // Brak wpisu (pierwsze logowanie w tej przeglądarce) = pusty punkt odniesienia,
      // czyli każda nieprzeczytana rozmowa jest nowa i dostaje kafel.
      this.unreadBaseline = this.readBaseline(id);
      this.unreadBaselineReady = true;

      // Świeży user (login / powrót po reloadzie): przeładuj listy i wymuś
      // ponowne pobranie historii otwartych okien z poprawnym `mine`.
      this.loadConversations();
      this.loadContacts();
      this.windows.forEach(w => {
        w.messages = [];
        w.loaded = false;
        // Porzuć dociąganie starszych z poprzedniego stanu — zgaszona flaga jest dla
        // `loadOlder` sygnałem, żeby odrzucić odpowiedź w locie (inaczej dokleiłaby
        // wiadomości do właśnie wyczyszczonej rozmowy).
        w.loadingOlder = false;
        w.noMoreOlder = false;
        this.loadMessages(w);
      });
      this.startConversationsPoll();
    });

    // Nowe wiadomości push z backendu.
    // ngZone.run(): callbacki socket.io przychodzą POZA strefą Angulara, więc sama
    // mutacja `win.messages` nie odpalała detekcji zmian — wiadomość dochodziła, ale
    // widok jej nie pokazywał do następnego zdarzenia (klik, odpowiedź HTTP, nawigacja).
    // Najbardziej bolało to na pełnoekranowym /chat, gdzie użytkownik tylko patrzy na
    // panel i nic innego detekcji nie wywoła — stąd „trzeba odświeżyć stronę".
    // Ten sam wzorzec stosują już voip-sidebar.component.ts i sip.service.ts.
    this.socketService.onChatMessage().subscribe(payload => this.ngZone.run(() => this.handleIncoming(payload)));
    this.socketService.onChatRead().subscribe(() => { /* opcjonalnie: aktualizacja „przeczytano" */ });
    this.socketService.onChatTyping().subscribe(payload => this.ngZone.run(() => this.handleTyping(payload)));
  }

  /** Emituje „piszę" do rozmówcy (throttle 2 s, best-effort przez socket). */
  notifyTyping(win: ChatWindow): void {
    const now = Date.now();
    if (now - this.lastTypingSent < 2000) { return; }
    this.lastTypingSent = now;
    this.socketService.emitChatTyping(win.peer.id);
  }

  private handleTyping(payload: any): void {
    const senderId = payload && (payload.senderId ?? payload.sender_id);
    if (senderId == null) { return; }
    const win = this.windows.find(w => String(w.peer.id) === String(senderId));
    if (!win) { return; }
    win.peerTyping = true;
    const key = String(senderId);
    if (this.typingTimers[key]) { clearTimeout(this.typingTimers[key]); }
    // Reset po 3,5 s bez kolejnego eventu (nadawca emituje co ~2 s podczas pisania).
    this.typingTimers[key] = setTimeout(() => { win.peerTyping = false; }, 3500);
  }

  loadConversations(onDone?: () => void): void {
    this.chatApi.getConversations().subscribe(
      list => {
        const convs = list || [];
        // Merge zamiast nadpisania: zachowaj kontakty bez rozmowy doklejone przez
        // loadContacts() — równoległe odpowiedzi przychodzą w dowolnej kolejności.
        const extras = this.contacts.filter(c =>
          !c.lastMessage && !convs.find(x => String(x.id) === String(c.id))
        );
        this.contacts = [...convs, ...extras];
        this.detectNewUnread(convs);
        this.syncWindowPeers();
        this.conversationsLoaded = true;
        if (onDone) { onDone(); }
      },
      () => { this.conversationsLoaded = true; if (onDone) { onDone(); } }
    );
  }

  /**
   * Porównuje świeżą listę rozmów z `unreadBaseline` i sygnalizuje te, w których licznik
   * nieprzeczytanych URÓSŁ — czyli te, do których faktycznie coś doszło od ostatniego
   * odczytu z backendu.
   *
   * Dlaczego wzrost, a nie `unread > 0`: kafel zamknięty przez użytkownika miałby wtedy
   * wracać przy każdym odpytaniu, a zaległe nieprzeczytane wysypywałyby się na ekran przy
   * każdym wejściu na stronę — czyli dokładnie to „wyskakiwanie bez powodu", którego
   * pozbyliśmy się przy `docked`.
   *
   * Punkt odniesienia jest TRWAŁY (`readBaseline`/`writeBaseline`), więc „wzrost" liczy się
   * także względem stanu sprzed wylogowania — wiadomość odebrana w czasie nieobecności
   * zapala kafel przy najbliższym zalogowaniu. Wcześniej pierwsza odpowiedź tylko zasiewała
   * punkt odniesienia i taka wiadomość przepadała bez śladu w doku.
   */
  private detectNewUnread(convs: ChatContact[]): void {
    if (!this.unreadBaselineReady) {
      // Zalogowany jeszcze nieznany (np. /chat zdążyło zawołać `loadConversations` przed
      // subskrypcją `currentUser`) — tylko zasiewamy stan w pamięci. Ta subskrypcja i tak
      // zaraz podmieni punkt odniesienia na trwały i przeładuje listę, więc nic nie ginie.
      convs.forEach(c => { this.unreadBaseline[String(c.id)] = c.unread || 0; });
      return;
    }
    let changed = false;
    convs.forEach(c => {
      const key = String(c.id);
      const now = c.unread || 0;
      const before = this.unreadBaseline[key] || 0;   // nowa rozmowa => 0, więc każde >0 to nowość
      if (now !== before) {
        this.unreadBaseline[key] = now;
        changed = true;
      }
      if (now > before) { this.signalUnread(c, now); }
    });
    if (changed) { this.writeBaseline(); }
  }

  /**
   * Punkt odniesienia zapamiętany z poprzedniej sesji tego użytkownika. `{}` = pierwszy raz
   * na tej przeglądarce (albo storage niedostępny/uszkodzony) — wtedy każda rozmowa
   * z nieprzeczytanymi jest traktowana jak nowa, co jest właściwym zachowaniem przy
   * pierwszym logowaniu.
   *
   * Klucz per użytkownik, bo dwa konta na jednej przeglądarce mają własne liczniki —
   * wspólny wpis wysypałby kafle drugiemu użytkownikowi na dzień dobry.
   */
  private readBaseline(userId: any): { [peerId: string]: number } {
    try {
      const raw = localStorage.getItem(this.UNREAD_BASELINE_KEY + String(userId));
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || typeof parsed !== 'object') { return {}; }
      // Przepisujemy przez Number(): uszkodzony/ręcznie zmieniony wpis nie może wpuścić
      // NaN-a ani stringa do porównania `now > before`.
      const out: { [peerId: string]: number } = {};
      Object.keys(parsed).forEach(k => {
        const n = Number(parsed[k]);
        if (isFinite(n) && n >= 0) { out[k] = n; }
      });
      return out;
    } catch (e) {
      return {};
    }
  }

  /** Zapis punktu odniesienia. Błąd (limit miejsca / tryb prywatny) nie może wywrócić czatu. */
  private writeBaseline(): void {
    if (this.currentUserId == null) { return; }
    try {
      localStorage.setItem(
        this.UNREAD_BASELINE_KEY + String(this.currentUserId),
        JSON.stringify(this.unreadBaseline)
      );
    } catch (e) {
      // Zapis nieudany — w tej sesji działamy na stanie z pamięci; po reloadzie kafel
      // może się powtórzyć, ale nic się nie gubi.
    }
  }

  /**
   * „Do tej rozmowy przyszło coś nowego" — pokaż to w doku. Wołane z `detectNewUnread`,
   * czyli z odczytu HTTP; ścieżka socketowa (`handleIncoming`) robi to samo natychmiast.
   * Obie są idempotentne: gdy okno już istnieje, nie powstaje drugie.
   */
  private signalUnread(peer: ChatContact, unread: number): void {
    const win = this.windows.find(w => String(w.peer.id) === String(peer.id));
    if (!win) { this.pushMinimizedWindow(peer, undefined, unread); return; }

    // Okno już jest — wystarczy, że trafi do doku (mogło powstać z przeglądania /chat).
    win.docked = true;
    // Licznik podnosimy TYLKO na kaflu. Przy oknie rozwiniętym użytkownik ma rozmowę
    // przed oczami, a `markRead` może jeszcze nie dolecieć do backendu — dopisywanie
    // badge'a zapalałoby go tuż po tym, jak wiadomość została przeczytana.
    if (win.minimized && unread > (win.unread || 0)) { win.unread = unread; }
  }

  /**
   * Cykliczne odświeżenie listy rozmów — patrz `CONVERSATIONS_POLL_MS`. Nie ma tu
   * `ngOnDestroy` (serwis jest singletonem `providedIn: 'root'`), ale wylogowanie
   * przeładowuje stronę (`NewHeaderComponent.finishLogout`), więc interwał ginie razem
   * z nią; `startConversationsPoll` pilnuje, żeby nie powstał drugi.
   */
  /**
   * Koniec sesji czatu — wylogowanie albo rola bez dostępu do czatu. Gasimy odpytywanie
   * i zerujemy stan, żeby kolejne logowanie zaczynało od czystej listy.
   *
   * `currentUserId` wraca do null celowo: bez tego ponowne zalogowanie TEGO SAMEGO
   * użytkownika trafiało w `String(id) === String(this.currentUserId)` i nie przeładowywało
   * rozmów. `contacts` czyścimy, bo z nich liczy się badge „Czat" — inaczej licznik
   * poprzedniej sesji zostawałby na ekranie następnej.
   */
  private endSession(): void {
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    this.pollInFlight = false;
    this.currentUserId = null;
    this.chatApi.currentUserId = null;
    this.contacts = [];
    // Nastepne zalogowanie ma znowu zaczac od szkieletu, a nie od pustej listy.
    this.conversationsLoaded = false;
  }

  private startConversationsPoll(): void {
    if (this.pollTimer) { return; }
    this.pollTimer = setInterval(() => {
      if (this.currentUserId == null || this.pollInFlight) { return; }
      this.pollInFlight = true;
      this.loadConversations(() => { this.pollInFlight = false; });
    }, this.CONVERSATIONS_POLL_MS);
  }

  loadContacts(): void {
    // Kontakty (z kim można pisać) — dołączamy do listy tych, z którymi nie ma jeszcze rozmowy.
    this.chatApi.getContacts().subscribe(
      peers => {
        (peers || []).forEach(p => {
          const existing = this.contacts.find(c => String(c.id) === String(p.id));
          if (!existing) {
            this.contacts.push({ ...p });
          } else if (!existing.avatar && p.avatar) {
            // Konwersacja mogła przyjść bez avatara (np. bump z socketu) — uzupełnij z API.
            existing.avatar = p.avatar;
          }
        });
        this.syncWindowPeers();
      },
      () => {}
    );
  }

  /**
   * Preferencja z poprzedniej sesji. Brak wpisu (albo niedostępny storage) = dymki
   * włączone, czyli zachowanie sprzed tej opcji.
   */
  private readDockEnabled(): boolean {
    try {
      return localStorage.getItem(this.DOCK_OFF_KEY) !== 'true';
    } catch (e) {
      return true;
    }
  }

  /**
   * Przełącza dymki czatu. Wołane z menu konta (`new-header`), bo przy wyłączeniu znika
   * CAŁY dok — przycisk w samym doku nie miałby jak go przywrócić.
   *
   * Włączenie zjeżdża każdą otwartą rozmowę na najnowszą wiadomość. Nie robimy tego
   * w komponencie: wystarczy wysłać ten sam sygnał, którym kończy się ładowanie historii
   * (`initial`), a gałąź `initial` w subskrypcjach `messageAdded` (chat-dock, chat-page)
   * już scrolluje na dół i gasi licznik „przejdź do najnowszych". `chat-page` te emisje
   * odfiltruje — reaguje wyłącznie na `activeConversation`.
   *
   * Tylko `expandedWindows`, bo tylko one mają wyrenderowaną listę wiadomości; kafle
   * dociągają historię dopiero przy rozwinięciu (`toggleMinimize`).
   */
  toggleDockEnabled(): void {
    this.setDockEnabled(!this.dockEnabled);
  }

  /** Jak wyżej, ale ustawia konkretny stan — używa tego `openChat` przy świadomym otwarciu rozmowy. */
  setDockEnabled(enabled: boolean): void {
    if (this.dockEnabled === enabled) { return; }
    this.dockEnabled = enabled;
    try {
      localStorage.setItem(this.DOCK_OFF_KEY, String(!enabled));
    } catch (e) {
      // Zapis nieudany (tryb prywatny / brak miejsca) — w tej sesji działa, po reloadzie wróci domyślne.
    }
    if (enabled) {
      this.expandedWindows.forEach(w => this.messageAdded.next({ win: w, mine: false, initial: true }));
    }
  }

  /** Uzupełnia peery otwartych okien danymi z kontaktów (avatar/nazwa mogły dociągnąć się z API później). */
  private syncWindowPeers(): void {
    this.windows.forEach(w => {
      const c = this.contacts.find(x => String(x.id) === String(w.peer.id));
      if (!c) { return; }
      if (c.name) { w.peer.name = c.name; }
      if (c.avatar) { w.peer.avatar = c.avatar; }
      if (c.online !== undefined) { w.peer.online = c.online; }
    });
  }

  toggleContacts(): void {
    this.showContacts = !this.showContacts;
    // Panel kontaktów siedzi w zwijanym rzędzie doku, więc przy zwiniętym doku samo
    // `showContacts = true` nic by nie pokazało. Uwaga: ta metoda NIE MA dziś żadnych
    // wywołań (panel kontaktów jest nieosiągalny) — gwarancja jest na przyszłość,
    // gdyby ktoś podłączył ją do menu.
    if (this.showContacts) { this.dockCollapsed = false; }
  }

  registerContact(peer: ChatPeer): void {
    const existing = this.contacts.find(c => String(c.id) === String(peer.id));
    if (existing) {
      existing.name = peer.name || existing.name;
      // Nie zamazuj avatara z API, gdy wołający go nie zna.
      existing.avatar = peer.avatar || existing.avatar;
      existing.online = peer.online;
    } else {
      this.contacts.unshift({ ...peer });
    }
  }

  openFromContacts(peer: ChatPeer): void {
    this.showContacts = false;
    this.openChat(peer);
  }

  /**
   * Zwraca (lub tworzy) okno konwersacji z peerem; przy pierwszym otwarciu pobiera historię.
   *
   * Wywoływane WYŁĄCZNIE z pełnoekranowego /chat (`selectConversation`) — zarówno przez
   * auto-wybór pierwszej rozmowy w `ngOnInit`, jak i przez klik w wiersz listy. Dlatego
   * nowe okno powstaje z `docked: false`: samo wejście na /chat (albo przeglądanie rozmów)
   * nie ma zostawiać kafla w doku po wyjściu ze strony. Istniejącego okna NIE odznaczamy —
   * rozmowa, która trafiła do doku z wiadomości, ma tam zostać.
   */
  getConversation(peer: ChatPeer): ChatWindow {
    let win = this.windows.find(w => String(w.peer.id) === String(peer.id));
    if (!win) {
      win = { peer, messages: [], minimized: true, unread: 0, draft: '', loaded: false,
              openOrder: ++this.windowSeq, docked: false };
      this.windows.push(win);
    } else {
      win.openOrder = ++this.windowSeq;   // użyta ponownie = znów „najnowsza"
    }
    win.unread = 0;
    this.loadMessages(win);
    return win;
  }

  selectConversation(contact: ChatContact): void {
    contact.unread = 0;
    this.activeConversation = this.getConversation(contact);
  }

  openChat(peer: ChatPeer): void {
    // Świadome otwarcie rozmowy rozwija dok — inaczej klik nie dawałby żadnego
    // widocznego efektu. Realny wywołujący: menu awatara opiekuna
    // (dashboard-header.component.ts). Zwinięcie respektują tylko wiadomości
    // przychodzące: te zostawiają dok zwinięty i podbijają badge.
    this.dockCollapsed = false;
    // Z tego samego powodu włączamy też dymki, jeśli użytkownik je wcześniej wyłączył:
    // dok jest wtedy ukryty w całości, więc klik w „Czat" przy awatarze nie dawałby
    // żadnego widocznego efektu. Wiadomości przychodzące tego NIE robią — mają zostać
    // niewidoczne, dokładnie o to chodzi w wyłączeniu dymków.
    this.setDockEnabled(true);
    let win = this.windows.find(w => String(w.peer.id) === String(peer.id));
    if (win) {
      win.minimized = false;
      win.unread = 0;
      win.docked = true;                  // świadome otwarcie — okno należy do doku
      win.openOrder = ++this.windowSeq;   // użyta ponownie = znów „najnowsza"
      // Wołający może mieć świeższe dane (np. avatar z API opiekunów w dashboard-header).
      if (peer.name) { win.peer.name = peer.name; }
      if (peer.avatar) { win.peer.avatar = peer.avatar; }
      if (peer.online !== undefined) { win.peer.online = peer.online; }
    } else {
      win = { peer, messages: [], minimized: false, unread: 0, draft: '', loaded: false,
              openOrder: ++this.windowSeq, docked: true };
      this.windows.unshift(win);
    }
    this.loadMessages(win);
    this.enforceLimit(win);
  }

  private loadMessages(win: ChatWindow): void {
    if (win.loaded) {
      this.markRead(win);
      // Ponowne otwarcie już załadowanej rozmowy — też zjedź na dół.
      // setTimeout: selectConversation/openChat ustawiają activeConversation i
      // minimized dopiero PO synchronicznym powrocie stąd — natychmiastowy emit
      // trafiałby w stary stan (komponent odrzuciłby event / kontener jeszcze
      // nie istnieje). Ścieżka świeżego ładowania jest async z natury.
      setTimeout(() => this.messageAdded.next({ win, mine: true, initial: true }), 0);
      return;
    }
    win.loaded = true;
    win.loadingHistory = true;
    this.chatApi.getMessages(win.peer.id, undefined, this.MESSAGES_PAGE_SIZE).subscribe(
      msgs => {
        win.loadingHistory = false;
        win.error = undefined;
        const history = msgs || [];
        const historyIds = history.map(m => String(m.id));
        const addedWhileLoading = win.messages.filter(m => historyIds.indexOf(String(m.id)) === -1);
        win.messages = history;
        // Świeża historia = znów jest co dociągać w górę. Bez tego resetu stan
        // z poprzedniego załadowania tej rozmowy blokowałby `loadOlder`.
        win.loadingOlder = false;
        win.noMoreOlder = false;
        this.markRead(win);
        if (addedWhileLoading.length) {
          win.messages = [...history, ...addedWhileLoading].sort((a, b) => a.createdAt - b.createdAt);
        }
        this.messageAdded.next({ win, mine: true, initial: true });
      },
      () => {
        win.loadingHistory = false;
        win.loaded = false;
        win.error = 'CHAT.HISTORY_FAILED';
      }
    );
  }

  /**
   * Dociąga STARSZĄ porcję historii (`GET …/messages?before=&limit=`) i dokleja ją na
   * początek `win.messages`. Wołane z komponentów przy przewinięciu listy pod sam
   * początek — patrz `onScroll` w chat-page i chat-dock.
   *
   * Świadomie NIE emituje `messageAdded`: ten strumień oznacza „doszła nowa wiadomość"
   * i zjeżdża na dół, a tutaj chodzi o dokładnie odwrotny kierunek. Pozycję scrolla
   * przywraca komponent w `onLoaded` — wołanym TYLKO wtedy, gdy coś faktycznie doszło.
   *
   * Założenie o backendzie (nie da się go sprawdzić z tego repo): `before` to ID
   * wiadomości, a odpowiedź zawiera wiadomości STARSZE od niej. Kolejność porządkujemy
   * u siebie (rosnąco po czasie), więc porcja oddana malejąco też wyświetli się dobrze.
   */
  loadOlder(win: ChatWindow, onLoaded?: () => void): void {
    // `!win.loaded` — pierwsza porcja jeszcze nie przyszła, nie ma od czego się odbić.
    if (!win || !win.loaded || win.loadingOlder || win.noMoreOlder) { return; }
    // Kotwicą może być tylko wiadomość ZNANA backendowi: optymistyczne `tmp-…` i te
    // bez ID odpadają, bo `before` wskazywałoby wtedy na nieistniejące ID.
    const oldest = win.messages.find(m => m.id != null && !String(m.id).startsWith('tmp-'));
    if (!oldest) { return; }

    win.loadingOlder = true;
    this.chatApi.getMessages(win.peer.id, oldest.id, this.MESSAGES_PAGE_SIZE).subscribe(
      msgs => {
        // Flaga zgaszona w międzyczasie = rozmowa została przeładowana od zera (patrz
        // reset w `init()`). Ta odpowiedź opisuje już nieistniejący stan — porzucamy ją.
        if (!win.loadingOlder) { return; }
        win.loadingOlder = false;

        const known = win.messages.map(m => String(m.id));
        const fresh = (msgs || [])
          .filter(m => m.id == null || known.indexOf(String(m.id)) === -1)
          .sort((a, b) => a.createdAt - b.createdAt);
        // Pusta porcja albo same duble = jesteśmy na początku rozmowy. Celowo NIE
        // sprawdzamy `length < MESSAGES_PAGE_SIZE`: backend ma prawo oddać mniej, niż
        // prosimy, a taki warunek uciąłby historię w połowie.
        if (!fresh.length) { win.noMoreOlder = true; return; }

        win.messages = [...fresh, ...win.messages];
        if (onLoaded) { onLoaded(); }
      },
      () => { win.loadingOlder = false; }   // błąd sieci — pozwól spróbować ponownie
    );
  }

  private markRead(win: ChatWindow): void {
    const last = win.messages.length ? win.messages[win.messages.length - 1] : null;
    if (last && last.id != null && !String(last.id).startsWith('tmp-')) {
      this.chatApi.markRead(win.peer.id, last.id).subscribe(() => {}, () => {});
    }
    win.unread = 0;
    const c = this.contacts.find(x => String(x.id) === String(win.peer.id));
    if (c) { c.unread = 0; }
  }

  closeChat(win: ChatWindow): void {
    this.windows = this.windows.filter(w => w !== win);
  }

  /**
   * Zamyka wszystkie dymki naraz — tyle samo, co kliknięcie „X" na każdym oknie doku po
   * kolei, tylko bez rozwijania kafli (kafel własnego „X" nie ma, więc dziś trzeba go
   * najpierw otworzyć). Konsekwencje jak w `closeChat`: rozmowa znika razem z wpisanym
   * szkicem i wraca dopiero przy nowej wiadomości albo świadomym otwarciu.
   *
   * Zdejmujemy WYŁĄCZNIE okna doku (`docked`). Rozmowy powstałe z samego przeglądania
   * listy na /chat nie są w doku widoczne, więc ich skasowanie byłoby efektem ubocznym
   * niewidocznym dla klikającego — a to na jednej z nich `chat-page` trzyma
   * `activeConversation`.
   */
  closeAllChats(): void {
    this.windows = this.windows.filter(w => !w.docked);
  }

  toggleMinimize(win: ChatWindow): void {
    win.minimized = !win.minimized;
    if (!win.minimized) {
      win.unread = 0;
      this.enforceLimit(win);
      // Rozwinięcie kafla = odczyt: markRead (API + liczniki) i event „initial",
      // po którym dok zjeżdża na dół — świeżo wyrenderowany kontener startuje
      // ze scrollTop=0, czyli od najstarszych załadowanych wiadomości.
      this.loadMessages(win);
    }
  }

  /** Wysyła wiadomość: optymistycznie dodaje ją i woła POST; po odpowiedzi podmienia ID. */
  send(win: ChatWindow): void {
    const text = (win.draft || '').trim();
    // Pusty draft albo treść ponad limit backendu (który zwraca 400) — nie wysyłamy.
    if (!text || text.length > this.MAX_MESSAGE_LENGTH) { return; }
    win.error = undefined;
    // Wysłanie wiadomości to świadome „prowadzę tę rozmowę" — od teraz okno należy
    // do doku, nawet jeśli powstało tylko z przeglądania listy na /chat.
    win.docked = true;
    const clientId = 'c' + (this.seq++) + '-' + Date.now();
    const optimistic: ChatMessage = {
      id: 'tmp-' + clientId, mine: true, body: text, createdAt: Date.now(), clientId, pending: true,
    };
    win.messages.push(optimistic);
    win.draft = '';
    this.bumpConversation(win.peer, text, false, optimistic.createdAt);
    this.messageAdded.next({ win, mine: true });

    this.chatApi.sendMessage(win.peer.id, text, clientId).subscribe(
      real => {
        optimistic.pending = false;
        if (real && real.id != null) { optimistic.id = real.id; if (real.createdAt) { optimistic.createdAt = real.createdAt; } }
      },
      () => this.markSendFailed(win, optimistic, 'CHAT.SEND_FAILED')
    );
  }

  /** Wysyła obraz: optymistyczny podgląd z lokalnego dataURL, potem POST i podmiana na presigned URL. */
  sendImage(win: ChatWindow, dataUrl: string, caption: string = ''): void {
    if (!dataUrl) { return; }
    win.docked = true;   // jak w `send()` — wysyłka „przypina" rozmowę do doku
    const clientId = 'c' + (this.seq++) + '-' + Date.now();
    const type = (dataUrl.substring(5, dataUrl.indexOf(';')) || 'image/*');
    const optimistic: ChatMessage = {
      id: 'tmp-' + clientId, mine: true, body: caption, createdAt: Date.now(),
      clientId, pending: true, attachmentUrl: dataUrl, attachmentType: type,
    };
    win.messages.push(optimistic);
    this.bumpConversation(win.peer, caption || '📷', false, optimistic.createdAt);
    this.messageAdded.next({ win, mine: true });

    this.chatApi.sendImage(win.peer.id, dataUrl, clientId, caption).subscribe(
      real => {
        optimistic.pending = false;
        if (real && real.id != null) { optimistic.id = real.id; }
        if (real && real.attachmentUrl) { optimistic.attachmentUrl = real.attachmentUrl; }
        if (real && real.createdAt) { optimistic.createdAt = real.createdAt; }
      },
      () => this.markSendFailed(win, optimistic, 'CHAT.ATTACHMENT_SEND_FAILED')
    );
  }

  /** Dwa poziomy: znacznik przy dymku mówi, KTÓRA wiadomość padła, komunikat w oknie — dlaczego. */
  private markSendFailed(win: ChatWindow, message: ChatMessage, errorKey: string): void {
    message.pending = false;
    message.error = true;
    win.error = errorKey;
  }

  // ===== załączniki: walidacja i przygotowanie =====

  /**
   * Przygotowuje plik do wysłania jako załącznik czatu.
   *
   * Wcześniej komponenty odrzucały po cichu wszystko poza PNG/JPEG ≤ 5 MB —
   * użytkownik klikał, wybierał plik i NIC się nie działo. Teraz:
   *  - PNG/JPEG w limicie idą bez zmian (ścieżka jak dotychczas),
   *  - pozostałe obrazy (WebP, GIF, zdjęcia z telefonu ponad limit) są
   *    przerysowywane na JPEG i zmniejszane, aż zmieszczą się w limicie,
   *  - czego przeglądarka nie umie zdekodować (np. HEIC z iPhone'a poza Safari)
   *    lub co nie jest obrazem — kończy się odrzuceniem z kluczem i18n.
   *
   * @returns dataURL gotowy dla `sendImage`; odrzucenie zwraca klucz tłumaczenia.
   */
  prepareAttachment(file: File): Promise<string> {
    if (!file) { return Promise.reject('CHAT.ATTACHMENT_UNREADABLE'); }
    if (!file.type || file.type.indexOf('image/') !== 0) {
      return Promise.reject('CHAT.ATTACHMENT_NOT_IMAGE');
    }
    const typeOk = this.ATTACHMENT_TYPES.indexOf(file.type) >= 0;

    // Limit liczymy na dataURL-u, czyli na tym, co faktycznie leci w body POST-a,
    // a nie na rozmiarze pliku — base64 jest ~33% większe i to ono decyduje,
    // czy serwer przyjmie żądanie.
    return this.readAsDataUrl(file).then(dataUrl =>
      (typeOk && dataUrl.length <= this.MAX_ATTACHMENT_BYTES) ? dataUrl : this.recodeToJpeg(dataUrl)
    );
  }

  private readAsDataUrl(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      // Brak onerror oznaczał ciszę, gdy pliku nie dało się odczytać
      // (np. zniknął z dysku sieciowego między wyborem a odczytem).
      reader.onerror = () => reject('CHAT.ATTACHMENT_UNREADABLE');
      reader.onload = () => {
        const result = String(reader.result || '');
        if (result) { resolve(result); } else { reject('CHAT.ATTACHMENT_UNREADABLE'); }
      };
      reader.readAsDataURL(file);
    });
  }

  /** Przerysowuje obraz na JPEG, zmniejszając go aż dataURL zmieści się w limicie. */
  private recodeToJpeg(dataUrl: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.onerror = () => reject('CHAT.ATTACHMENT_UNSUPPORTED');
      img.onload = () => {
        if (!img.width || !img.height) { reject('CHAT.ATTACHMENT_UNSUPPORTED'); return; }

        const draw = (scale: number, quality: number): string => {
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(img.width * scale));
          canvas.height = Math.max(1, Math.round(img.height * scale));
          const ctx = canvas.getContext('2d');
          if (!ctx) { return ''; }
          // JPEG nie ma kanału alfa — bez podkładu przezroczyste PNG wyszłoby czarne.
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          return canvas.toDataURL('image/jpeg', quality);
        };

        let scale = Math.min(1, this.ATTACHMENT_MAX_DIMENSION / Math.max(img.width, img.height));
        let quality = 0.85;
        let out = draw(scale, quality);
        // Najpierw jakość (mniej widoczne), potem dopiero wymiary.
        for (let i = 0; out && out.length > this.MAX_ATTACHMENT_BYTES && i < 8; i++) {
          if (quality > 0.5) { quality -= 0.15; } else { scale *= 0.75; }
          out = draw(scale, quality);
        }

        if (!out) { reject('CHAT.ATTACHMENT_UNSUPPORTED'); return; }
        if (out.length > this.MAX_ATTACHMENT_BYTES) { reject('CHAT.ATTACHMENT_TOO_BIG'); return; }
        resolve(out);
      };
      img.src = dataUrl;
    });
  }

  // ===== realtime =====

  private handleIncoming(payload: any): void {
    if (!payload) { return; }
    // Bez znanego ID zalogowanego nie da się poprawnie rozstrzygnąć mine/otherId —
    // przetwarzanie na ślepo tworzyło sztuczne konwersacje. Odśwież listę i czekaj.
    if (this.currentUserId == null) { this.loadConversations(); return; }
    const m = payload.message || payload;
    const senderId = m.senderId ?? m.sender_id ?? m.sender;
    const peerField = m.peerId ?? m.peer_id ?? payload.peerId ?? payload.peer_id;
    const mine = String(senderId) === String(this.currentUserId);
    const otherId = mine ? peerField : senderId;
    if (otherId == null) { return; }
    // Nigdy nie twórz/aktualizuj rozmowy „z samym sobą" (zdegenerowany payload).
    if (String(otherId) === String(this.currentUserId)) { return; }

    const body = m.body || m.text || m.message || '';
    const createdAt = (m.createdAt || m.created_at) ? new Date(m.createdAt || m.created_at).getTime() : Date.now();
    const msg: ChatMessage = {
      id: m.id, mine, body, createdAt,
      attachmentUrl: m.attachmentUrl || m.attachment_url || undefined,
      attachmentType: m.attachmentType || m.attachment_type || undefined,
    };

    const win = this.windows.find(w => String(w.peer.id) === String(otherId));
    if (win) {
      // dedupe: po ID oraz po clientId (echo własnej optymistycznej wiadomości).
      // String(): node przekazuje id z query params jako string, API daje number.
      if (msg.id != null && win.messages.some(x => String(x.id) === String(msg.id))) { /* już mamy */ }
      else if (mine && (m.clientId || m.client_id)) {
        const cid = m.clientId || m.client_id;
        const opt = win.messages.find(x => x.clientId === cid);
        if (opt) { opt.id = msg.id; opt.pending = false; }
        else { win.messages.push(msg); this.messageAdded.next({ win, mine }); }
      } else {
        win.messages.push(msg);

        // Licznik nieprzeczytanych rośnie dla KAŻDEJ obcej wiadomości — również w rozmowie
        // otwartej na ekranie (decyzja produktowa). Gasi go dopiero KLIKNIĘCIE wiersza:
        // selectConversation() → getConversation() zeruje `win.unread`, a loadMessages()
        // → markRead() zeruje `c.unread` i potwierdza przeczytanie na backendzie.
        // Świadomie NIE ma tu automatycznego markRead(): wcześniejsza wersja oznaczała
        // otwartą rozmowę jako przeczytaną, więc licznik nigdy się nie pojawiał.
        // Obca wiadomość „przypina" rozmowę do doku — także wtedy, gdy okno powstało
        // tylko z przeglądania /chat (`docked: false`). Bez tego kafel z licznikiem
        // pojawiałby się wyłącznie dla rozmów, które nie miały jeszcze okna.
        if (!mine) { win.unread++; win.docked = true; }

        this.messageAdded.next({ win, mine });
      }
    }
    const preview = (msg.attachmentUrl && !body) ? '📷' : body;
    this.bumpConversation({ id: otherId, name: '' }, preview, !mine, msg.createdAt);

    // Wiadomość od rozmowy BEZ otwartego okna nie dawała wcześniej żadnego sygnału
    // w doku — powyższe `if (win)` po prostu jej nie dopisywało, a licznik na liście
    // konwersacji widać tylko na /chat. Tworzymy okno ZMINIMALIZOWANE: dok pokazuje
    // kafel z licznikiem, ale nic nie wyskakuje na wierzch pracy operatora.
    if (!win && !mine) { this.ensureMinimizedWindow(otherId, msg); }
  }

  /**
   * Tworzy zminimalizowane okno dla przychodzącej wiadomości od rozmowy, która nie
   * ma jeszcze okna. Nazwę i awatar bierzemy z listy kontaktów — payload socketu nie
   * jest tu wiarygodny (patrz `bumpConversation`), a bez nich powstałby kafel „?”.
   */
  private ensureMinimizedWindow(peerId: any, msg: ChatMessage): void {
    const contact = this.contacts.find(c => String(c.id) === String(peerId));
    if (contact) { this.pushMinimizedWindow(contact, msg); return; }

    // Nieznany rozmówca (kontakty jeszcze się nie wczytały albo ktoś nowy) —
    // dociągnij listę i spróbuj ponownie, gdy znamy już nazwę.
    this.loadConversations(() => {
      const fresh = this.contacts.find(c => String(c.id) === String(peerId));
      if (fresh) { this.pushMinimizedWindow(fresh, msg); }
    });
  }

  /**
   * @param msg    wiadomość z socketu, jeśli ją mamy. Ścieżka zapasowa (`signalUnread`
   *               z odpytania HTTP) zna tylko licznik, więc okno powstaje puste —
   *               i tak ma `loaded: false`, więc treść dociąga się przy rozwinięciu.
   * @param unread ile nieprzeczytanych pokazać na kaflu (z socketu zawsze 1).
   */
  private pushMinimizedWindow(peer: ChatPeer, msg?: ChatMessage, unread: number = 1): void {
    // Wyścig: okno mogło powstać między eventem a callbackiem loadConversations.
    if (this.windows.find(w => String(w.peer.id) === String(peer.id))) { return; }
    this.windows.push({
      peer: { id: peer.id, name: peer.name, avatar: peer.avatar, online: peer.online },
      messages: msg ? [msg] : [],
      minimized: true,
      unread: unread,
      draft: '',
      openOrder: ++this.windowSeq,
      docked: true,   // okno powstałe z wiadomości istnieje właśnie po to, żeby być w doku
      // Celowo `false`: historii jeszcze nie mamy, a pobranie jej tutaj wołałoby
      // markRead() i od razu zgasiłoby licznik. Dociągamy przy rozwinięciu kafla.
      loaded: false,
    });
  }

  // Aktualizuje listę konwersacji: podnosi na górę, ustawia ostatnią wiadomość, czas i licznik.
  // `createdAt` (ms) jest opcjonalny tylko dla zgodności — podają go wszyscy wołający.
  private bumpConversation(
    peer: ChatPeer,
    lastMessage: string,
    incrementUnread: boolean = false,
    createdAt?: number,
  ): void {
    let c = this.contacts.find(x => String(x.id) === String(peer.id));
    if (!c) {
      if (!peer.name) {
        // Nieznany rozmówca bez nazwy (np. event socketu zanim załadowały się
        // kontakty) — nie tworzymy pozycji „?"; dociągamy pełne dane z API.
        this.loadConversations();
        return;
      }
      c = { id: peer.id, name: peer.name, avatar: peer.avatar, online: peer.online, unread: 0 };
      this.contacts.unshift(c);
    } else {
      this.contacts = [c, ...this.contacts.filter(x => x !== c)];
    }
    c.lastMessage = lastMessage;
    // Czas ostatniej wiadomości na liście — wcześniej NIE był aktualizowany, więc wiersz
    // pokazywał godzinę z ostatniego GET /chat/conversations i „zamarzał", mimo że podpis
    // (lastMessage) się zmieniał. Zapisujemy w tym samym formacie, w jakim przychodzi
    // z backendu (ISO), żeby oba źródła renderowały się jednakowo.
    if (createdAt) { c.time = new Date(createdAt).toISOString(); }
    // Bez wyjątku dla aktywnej rozmowy: licznik ma rosnąć także dla tej otwartej na
    // ekranie i gasnąć wyłącznie po kliknięciu wiersza (patrz handleIncoming). Wcześniej
    // warunek pomijał aktywną rozmowę, więc na /chat licznik w ogóle się nie pojawiał.
    if (incrementUnread) { c.unread = (c.unread || 0) + 1; }
  }

  /**
   * Okna NALEŻĄCE do doku — czyli te, które użytkownik świadomie otworzył, w których coś
   * wysłał albo w których coś dostał (`docked`). Rozmowy powstałe wyłącznie z przeglądania
   * listy na /chat są tu pominięte: wcześniej samo wejście na tę stronę (jej `ngOnInit`
   * auto-wybiera pierwszą konwersację) zostawiało po wyjściu kafel w doku, mimo że nie
   * padła ani jedna wiadomość. `windows` zostaje pełne — historia i szkic przeglądanych
   * rozmów są dalej pod ręką, gdy któraś trafi do doku później.
   */
  get dockWindows(): ChatWindow[] {
    return this.windows.filter(w => w.docked);
  }

  /**
   * Okna, które dok ma prawo pokazać: najwyżej `MAX_DOCK_WINDOWS` najnowszych.
   * Zwracamy je w PIERWOTNEJ kolejności tablicy `windows` — to ona steruje układem
   * kafli (kolumna jest `column-reverse`), więc samo sortowanie po `openOrder`
   * przetasowałoby stos przy każdym otwarciu.
   */
  private get visibleWindows(): ChatWindow[] {
    const docked = this.dockWindows;
    if (docked.length <= this.MAX_DOCK_WINDOWS) { return docked; }
    const keep = [...docked]
      .sort((a, b) => (b.openOrder || 0) - (a.openOrder || 0))
      .slice(0, this.MAX_DOCK_WINDOWS);
    return docked.filter(w => keep.indexOf(w) !== -1);
  }

  /**
   * Najnowsze (ostatnio użyte) okno z podanej listy — jako tablica 0- lub
   * 1-elementowa, żeby wynik dawał się wprost zwrócić z getterów szablonu.
   */
  private newestOf(list: ChatWindow[]): ChatWindow[] {
    if (!list.length) { return []; }
    return [list.reduce((a, b) => ((b.openOrder || 0) > (a.openOrder || 0) ? b : a))];
  }

  /**
   * Rozwinięte okna rozmów. **Nie zależy od `dockCollapsed`** — zwijanie chowa wyłącznie
   * kafle (patrz `minimizedWindows`), a otwarta rozmowa zostaje na ekranie. Wcześniej
   * zwinięcie redukowało tę listę do jednego (ostatnio otwartego) okna, przez co
   * chowało także rozmowę, w której użytkownik akurat pisał.
   */
  get expandedWindows(): ChatWindow[] {
    return this.visibleWindows.filter(w => !w.minimized).slice(0, this.MAX_EXPANDED);
  }

  get minimizedWindows(): ChatWindow[] {
    const expanded = this.expandedWindows;
    const rest = this.visibleWindows.filter(w => expanded.indexOf(w) === -1);
    if (!this.dockCollapsed) { return rest; }
    // Zwinięty dok chowa kafle — ale gdy NIE MA ani jednego rozwiniętego okna
    // (wszystkie przyszły z socketu jako kafle), zostawiamy najnowszy kafel, żeby po
    // zwinięciu nie został sam przycisk bez żadnego kontekstu.
    return expanded.length ? [] : this.newestOf(rest);
  }

  /**
   * Czy „rolka" ma co schować. Steruje widocznością przycisku zwijania: jeśli zwinięcie
   * niczego by nie ukryło, przycisk jest atrapą i go nie pokazujemy.
   *
   * Zwinięcie chowa WYŁĄCZNIE kafle (okna zostają — patrz `expandedWindows`), więc liczy
   * się sama liczba kafli `rest`, ale próg zależy od tego, czy jest jakieś rozwinięte okno
   * — dokładnie tak, jak rozstrzyga to `minimizedWindows`:
   *
   * | rozwinięte okna | co zostaje po zwinięciu | przycisk ma sens, gdy |
   * |---|---|---|
   * | są          | zero kafli                              | `rest > 0` |
   * | nie ma      | najnowszy kafel (żeby nie został sam przycisk) | `rest > 1` |
   *
   * Drugi wiersz to poprawka: przy JEDNEJ rozmowie w doku (typowo kafel z wiadomości)
   * `rest === 1`, a `minimizedWindows` i tak zwraca ten kafel z powrotem — kliknięcie
   * przycisku nie dawało więc żadnego efektu. Wcześniejszy warunek (`rest > 0`) tego
   * nie rozróżniał i pokazywał przycisk nad pojedynczym kaflem.
   *
   * Liczone z `visibleWindows` i `expandedWindows`, a **nie** z `minimizedWindows.length`
   * — ten ostatni przy zwiniętym doku zwraca pustą listę (kafle są właśnie schowane), więc
   * przycisk zniknąłby zaraz po kliknięciu i nie dałoby się rozwinąć z powrotem. Oba użyte
   * składniki są niezależne od `dockCollapsed`, więc wynik jest ten sam po obu stronach
   * przełącznika. Łapie też rozmowy wypchnięte na kafel przez limit `MAX_EXPANDED`
   * (mają `minimized === false`).
   */
  get hasCollapsibleTiles(): boolean {
    const expanded = this.expandedWindows.length;
    const rest = this.visibleWindows.length - expanded;
    return expanded ? rest > 0 : rest > 1;
  }

  /**
   * Liczba nieprzeczytanych w oknach, których dok TERAZ NIE POKAZUJE — badge na
   * przycisku zwijania. Bez tego zwinięcie gasiłoby jedyny sygnał o nowych
   * wiadomościach. Świadomie pomijamy okna widoczne (po zwinięciu są to wszystkie
   * rozwinięte okna): ich wiadomości widać na ekranie, a kafle mają własny badge.
   */
  get dockUnread(): number {
    const shown = this.expandedWindows.concat(this.minimizedWindows);
    // Liczymy po `dockWindows`, nie po `windows` — okna z samego przeglądania /chat nigdy
    // nie są pokazywane w doku, więc doliczanie ich do badge'a obiecywałoby coś,
    // czego rozwinięcie doku by nie odsłoniło.
    return this.dockWindows.reduce((sum, w) => sum + (shown.indexOf(w) === -1 ? (w.unread || 0) : 0), 0);
  }

  /**
   * Suma nieprzeczytanych ze WSZYSTKICH rozmów — badge przy pozycji „Czat" w menu konta.
   *
   * Liczona z `contacts`, a nie z `windows`: okno istnieje tylko dla rozmów otwartych
   * w tej sesji, więc suma po oknach gubiłaby wiadomości z rozmów, których użytkownik
   * jeszcze nie dotknął — czyli dokładnie te, o których badge ma informować. To ta sama
   * liczba, którą widać przy wierszu na /chat, więc oba miejsca nie mogą się rozjechać.
   *
   * Endpoint `GET /chat/unread-count` (ChatApiService) celowo pominięty: wymagałby
   * pollingu i byłby drugim, niezależnym źródłem tej samej liczby. `contacts` są
   * utrzymywane na bieżąco — podbija je socket (`bumpConversation`), zeruje otwarcie
   * rozmowy (`markRead`), a `loadConversations()` nadpisuje je stanem z backendu.
   *
   * Uwaga: gdy backend odrzuci `GET /chat/conversations` (np. rola bez czatu), lista
   * zostaje pusta i getter zwraca 0 — badge się po prostu nie pokaże.
   */
  get unreadTotal(): number {
    return this.contacts.reduce((sum, c) => sum + (c.unread || 0), 0);
  }

  private enforceLimit(active: ChatWindow): void {
    const expanded = this.windows.filter(w => !w.minimized);
    if (expanded.length <= this.MAX_EXPANDED) { return; }
    for (let i = expanded.length - 1; i >= 0 && this.windows.filter(w => !w.minimized).length > this.MAX_EXPANDED; i--) {
      if (expanded[i] !== active) { expanded[i].minimized = true; }
    }
  }
}
